
import { Answer } from '../types/assessmentTypes';
import { Category, Question } from '@/types/assessment';
import { calculateMaturityLevel, getMaturityLevelLabel } from '@/utils/maturityLevelUtils';
import { MaturityScoreResult } from '../types/responseTypes';

/**
 * Calculate maturity scores for each category based on responses
 */
export const calculateMaturityScores = (
  responses: Record<string, any>, 
  questions: Question[], 
  categories: Category[]
): Record<string, MaturityScoreResult> => {
  const maturityScores: Record<string, MaturityScoreResult> = {};
  
  categories.forEach(category => {
    // Find questions that belong to this category
    const categoryQuestions = questions.filter(q => {
      // Check if the question belongs to this category using category.id
      return category.questions.some(catQ => catQ.id === q.id);
    });
    
    let currentTotal = 0;
    let targetTotal = 0;
    let count = 0;
    
    categoryQuestions.forEach(question => {
      const response = responses[question.id];
      
      if (response) {
        // Handle different response formats for CURRENT value
        let currentVal = 0;
        if (typeof response.current === 'number') {
          currentVal = response.current;
        } else if (Array.isArray(response.current)) {
          if (response.current.length > 0) {
            // Map current answer IDs to their numeric values
            const currentValues = response.current.map((id: string) => {
              const answer = question.answers.find(a => a.id === id);
              return answer ? answer.value : 0;
            });
            currentVal = Math.max(...currentValues);
          } else {
            currentVal = 0;
          }
        } else if (response.value !== undefined) {
          currentVal = response.value;
        } else {
          currentVal = 0;
        }
              
        // Handle different response formats for TARGET value
        let targetVal = 0;
        if (typeof response.target === 'number') {
          targetVal = response.target;
        } else if (Array.isArray(response.target)) {
          if (response.target.length > 0) {
            // Map target answer IDs to their numeric values
            const targetValues = response.target.map((id: string) => {
              const answer = question.answers.find(a => a.id === id);
              return answer ? answer.value : 0;
            });
            targetVal = Math.max(...targetValues);
          } else {
            targetVal = 4; // Default target for maturity
          }
        } else {
          targetVal = 4; // Default target for maturity
        }
        
        currentTotal += currentVal;
        targetTotal += targetVal;
        count++;
      }
    });
    
    if (count > 0) {
      const avgScore = currentTotal / count;
      const level = calculateMaturityLevel(avgScore);
      
      maturityScores[category.id] = {
        category: category.id,
        score: avgScore,
        level,
        current: avgScore,
        target: targetTotal / count,
        label: getMaturityLevelLabel(level)
      };
    }
  });
  
  return maturityScores;
};
